import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  Plus, Lock, Unlock, ChevronRight, ArrowLeft, CheckSquare, Square,
  AlertCircle, CheckCircle2, X, Trash2, Calendar, Loader2,
} from "lucide-react";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const Modal = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* Start-recon form */
const StartReconForm = ({ bookId, accounts, lang, onCancel, onStarted }) => {
  const bankAccounts = accounts.filter((a) => !a.is_header && a.is_active && (a.subtype === "bank" || a.code.startsWith("10")));
  const [form, setForm] = useState({
    cash_account_code: bankAccounts[0]?.code || "1010",
    period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    period_end: todayISO(),
    beginning_balance: 0,
    ending_balance: 0,
    statement_ref: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/reconciliations`, {
        ...form,
        beginning_balance: parseFloat(form.beginning_balance),
        ending_balance: parseFloat(form.ending_balance),
      });
      toast.success("Reconciliation started");
      onStarted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="start-recon-form">
      <div>
        <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Cuenta de Banco" : "Bank Account"}</label>
        <select required value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })} className="rn3-input" data-testid="sr-account">
          {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {lang === "es" && a.name_es ? a.name_es : a.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Inicio del Período" : "Period Start"}</label>
          <input required type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rn3-input" data-testid="sr-start" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Fin del Período" : "Period End"}</label>
          <input required type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rn3-input" data-testid="sr-end" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Balance Inicial" : "Beginning Balance"}</label>
          <input required type="number" step="0.01" value={form.beginning_balance} onChange={(e) => setForm({ ...form, beginning_balance: e.target.value })} className="rn3-input" data-testid="sr-begin" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Balance Final del Estado" : "Statement Ending Balance"}</label>
          <input required type="number" step="0.01" value={form.ending_balance} onChange={(e) => setForm({ ...form, ending_balance: e.target.value })} className="rn3-input" data-testid="sr-end-bal" />
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Referencia del Estado" : "Statement Reference"}</label>
        <input value={form.statement_ref} onChange={(e) => setForm({ ...form, statement_ref: e.target.value })} className="rn3-input" placeholder="e.g. Chase August 2025" data-testid="sr-ref" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="sr-submit">{busy ? "…" : (lang === "es" ? "Iniciar" : "Start Reconciliation")}</button>
      </div>
    </form>
  );
};

/* Active reconciliation view */
const ActiveRecon = ({ bookId, reconId, accounts, lang, onBack, onFinalized }) => {
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${bookId}/reconciliations/${reconId}`);
      setRecon(data);
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reconId]);

  const accountLabel = useMemo(() => {
    if (!recon) return "";
    const a = accounts.find((x) => x.code === recon.cash_account_code);
    if (!a) return recon.cash_account_code;
    return `${a.code} — ${lang === "es" && a.name_es ? a.name_es : a.name}`;
  }, [recon, accounts, lang]);

  const toggle = async (txn) => {
    try {
      await api.patch(`/back-office/books/${bookId}/reconciliations/${reconId}/clear/${txn.id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const finalize = async () => {
    if (!recon?.is_balanced) { toast.error("Cannot finalize until difference is $0.00"); return; }
    if (!window.confirm("Lock this reconciliation? Cleared transactions will be marked Reconciled and cannot be edited.")) return;
    setBusy(true);
    try {
      await api.post(`/back-office/books/${bookId}/reconciliations/${reconId}/finalize`);
      toast.success("Reconciliation locked");
      onFinalized();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const abandon = async () => {
    if (!window.confirm("Discard this reconciliation? Cleared transactions will be unmarked.")) return;
    try {
      await api.delete(`/back-office/books/${bookId}/reconciliations/${reconId}`);
      toast.success("Reconciliation discarded");
      onBack();
    } catch (err) { toast.error("Failed"); }
  };

  if (loading) return <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  if (!recon) return null;
  const locked = recon.status === "locked";

  return (
    <div className="space-y-6" data-testid="active-recon">
      <button onClick={onBack} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="recon-back">
        <ArrowLeft className="w-3 h-3" /> {lang === "es" ? "Volver" : "Back to Reconciliations"}
      </button>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="rn3-eyebrow flex items-center gap-2">
            {locked ? <Lock className="w-3 h-3 text-[#0A0A0A]" /> : <Unlock className="w-3 h-3 text-[#C4A252]" />}
            {locked ? (lang === "es" ? "Bloqueada" : "Locked") : (lang === "es" ? "Abierta" : "Open")}
          </div>
          <h2 className="font-serif-display text-3xl mt-2">{accountLabel}</h2>
          <div className="text-sm text-[#737373] mt-1">
            {recon.period_start} → {recon.period_end} {recon.statement_ref && `· ${recon.statement_ref}`}
          </div>
        </div>
        {!locked && (
          <div className="flex gap-2">
            <button onClick={abandon} className="text-xs text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="recon-abandon">
              <Trash2 className="w-3 h-3" /> {lang === "es" ? "Descartar" : "Discard"}
            </button>
            <button onClick={finalize} disabled={busy || !recon.is_balanced} className="rn3-btn-primary" data-testid="recon-finalize">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {lang === "es" ? "Finalizar" : "Finalize & Lock"}
            </button>
          </div>
        )}
      </div>

      {/* Balance summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="recon-summary">
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#737373]">{lang === "es" ? "Balance Inicial" : "Beginning Balance"}</div>
          <div className="font-serif-display text-2xl mt-2">{fmt(recon.beginning_balance)}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#737373]">{lang === "es" ? "Balance Final" : "Ending Balance"}</div>
          <div className="font-serif-display text-2xl mt-2">{fmt(recon.ending_balance)}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#C4A252]">{lang === "es" ? "Cambio Esperado" : "Expected Change"}</div>
          <div className="font-serif-display text-2xl mt-2">{fmt(recon.expected_change)}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#15803D]">{lang === "es" ? "Confirmadas" : "Cleared Total"}</div>
          <div className="font-serif-display text-2xl mt-2">{fmt(recon.cleared_sum)}</div>
          <div className="text-xs text-[#737373] mt-1">{recon.cleared_count}/{recon.transactions.length}</div>
        </div>
        <div className={`rn3-card ${recon.is_balanced ? "border-[#15803D]" : "border-[#8B1C22]"}`}>
          <div className={`text-xs uppercase tracking-widest ${recon.is_balanced ? "text-[#15803D]" : "text-[#8B1C22]"}`}>{lang === "es" ? "Diferencia" : "Difference"}</div>
          <div className={`font-serif-display text-2xl mt-2 ${recon.is_balanced ? "text-[#15803D]" : "text-[#8B1C22]"}`}>
            {fmt(recon.difference)}
          </div>
          <div className="text-xs mt-1 inline-flex items-center gap-1">
            {recon.is_balanced ? <><CheckCircle2 className="w-3 h-3 text-[#15803D]" /> {lang === "es" ? "Balanceada" : "Balanced"}</> : <><AlertCircle className="w-3 h-3 text-[#8B1C22]" /> {lang === "es" ? "No balanceada" : "Not balanced"}</>}
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]" data-testid="recon-txns-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <tr>
              <th className="py-2 px-3 w-16 text-center">{lang === "es" ? "Conc." : "Clear"}</th>
              <th className="py-2 px-3 w-28">{lang === "es" ? "Fecha" : "Date"}</th>
              <th className="py-2 px-3">{lang === "es" ? "Descripción" : "Description"}</th>
              <th className="py-2 px-3 text-right w-32">{lang === "es" ? "Monto" : "Amount"}</th>
            </tr>
          </thead>
          <tbody>
            {recon.transactions.length === 0 && (
              <tr><td colSpan="4" className="py-10 text-center text-[#737373]">
                {lang === "es" ? "Sin transacciones en este período para esta cuenta." : "No transactions for this account in this period."}
              </td></tr>
            )}
            {recon.transactions.map((t) => (
              <tr key={t.id} className={`border-b border-[#F3EFE9] ${t.cleared ? "bg-[#FDFBF7]" : ""}`} data-testid={`recon-row-${t.id}`}>
                <td className="py-2 px-3 text-center">
                  <button
                    disabled={locked}
                    onClick={() => toggle(t)}
                    className={`inline-flex items-center ${locked ? "cursor-not-allowed opacity-50" : "hover:scale-110"}`}
                    data-testid={`recon-clear-${t.id}`}
                  >
                    {t.cleared
                      ? <CheckSquare className="w-5 h-5 text-[#15803D]" />
                      : <Square className="w-5 h-5 text-[#737373]" />}
                  </button>
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{t.date}</td>
                <td className="py-2 px-3">{t.description}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${t.amount < 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(t.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* Top-level Reconciliation Tab */
const ReconciliationTab = ({ book, accounts, lang }) => {
  const [recons, setRecons] = useState([]);
  const [showStart, setShowStart] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/reconciliations`);
      setRecons(data);
    } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  if (activeId) {
    return <ActiveRecon
      bookId={book.id} reconId={activeId} accounts={accounts} lang={lang}
      onBack={() => { setActiveId(null); load(); }}
      onFinalized={() => { setActiveId(null); load(); }}
    />;
  }

  return (
    <div className="space-y-6" data-testid="recon-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-3xl">{lang === "es" ? "Conciliaciones Bancarias" : "Bank Reconciliations"}</h2>
          <p className="text-sm text-[#737373] mt-1">
            {lang === "es"
              ? "Empareja tus libros con el estado de cuenta del banco."
              : "Match your books against the bank statement."}
          </p>
        </div>
        <button onClick={() => setShowStart(true)} className="rn3-btn-primary" data-testid="recon-new-btn">
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nueva Conciliación" : "New Reconciliation"}
        </button>
      </div>

      {loading && <div className="text-sm text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
      {!loading && recons.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[#E5E5E5]" data-testid="recon-empty">
          <Calendar className="w-10 h-10 mx-auto mb-3 text-[#C4A252]" />
          <p className="text-sm text-[#737373]">
            {lang === "es" ? "Sin conciliaciones aún. Inicia la primera arriba." : "No reconciliations yet. Start your first one above."}
          </p>
        </div>
      )}

      <div className="space-y-3" data-testid="recon-list">
        {recons.map((r) => {
          const acc = accounts.find((a) => a.code === r.cash_account_code);
          const accLabel = acc ? `${acc.code} — ${lang === "es" && acc.name_es ? acc.name_es : acc.name}` : r.cash_account_code;
          const locked = r.status === "locked";
          return (
            <button
              key={r.id}
              onClick={() => setActiveId(r.id)}
              className={`w-full text-left border ${locked ? "border-[#E5E5E5]" : "border-[#C4A252]"} bg-white hover:bg-[#FDFBF7] px-5 py-4 flex items-center gap-4`}
              data-testid={`recon-card-${r.id}`}
            >
              {locked ? <Lock className="w-5 h-5 text-[#0A0A0A] shrink-0" /> : <Unlock className="w-5 h-5 text-[#C4A252] shrink-0" />}
              <div className="flex-1">
                <div className="font-medium">{accLabel}</div>
                <div className="text-xs text-[#737373] mt-0.5">
                  {r.period_start} → {r.period_end}
                  {r.statement_ref && ` · ${r.statement_ref}`}
                  {locked && ` · ${lang === "es" ? "Finalizada" : "Finalized"} ${r.finalized_at?.slice(0,10)}`}
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <div className="text-xs uppercase tracking-widest text-[#737373]">{lang === "es" ? "Balance Final" : "Ending"}</div>
                <div className="font-serif-display text-xl">{fmt(r.ending_balance)}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${locked ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-[#C4A252] text-[#8B1C22]"}`}>
                {locked ? (lang === "es" ? "Bloqueada" : "Locked") : (lang === "es" ? "Abierta" : "Open")}
              </span>
              <ChevronRight className="w-4 h-4 text-[#737373]" />
            </button>
          );
        })}
      </div>

      <Modal open={showStart} onClose={() => setShowStart(false)} title={lang === "es" ? "Nueva Conciliación" : "Start a Reconciliation"} testid="start-recon-modal">
        <StartReconForm
          bookId={book.id} accounts={accounts} lang={lang}
          onCancel={() => setShowStart(false)}
          onStarted={(r) => { setShowStart(false); load(); setActiveId(r.id); }}
        />
      </Modal>
    </div>
  );
};

export default ReconciliationTab;
